import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft, X, Plus, Loader2, CheckCircle2, XCircle, Circle,
  AlertCircle, ChevronDown,
  UserPlus, User, ShieldCheck, GraduationCap, Briefcase,
  KeyRound, Star,
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { userService, organizationService } from '../../services';
import useEmailCheck from '../../hooks/useEmailCheck';

/**
 * Add User Page
 * Form to create a new user in the system
 */

// Shared chrome — kept in lockstep with AddOrganizationPage so the two
// forms read as a single design system.
const CARD_BASE =
  'rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04)]';
const CARD_FORM =
  'rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-12px_rgba(16,24,40,0.08)] overflow-hidden';

const PREVIEW_AVATAR_GRADIENTS = [
  'from-primary-500 to-primary-700',
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-teal-600',
  'from-violet-500 to-purple-600',
  'from-amber-500 to-orange-600',
  'from-cyan-500 to-sky-600',
];
const seededPreviewGradient = (seed = '') => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) & 0x7fffffff;
  return PREVIEW_AVATAR_GRADIENTS[hash % PREVIEW_AVATAR_GRADIENTS.length];
};

const passwordStrength = (pw = '') => {
  let s = 0;
  if (pw.length >= 8) s += 1;
  if (/[A-Z]/.test(pw)) s += 1;
  if (/[0-9]/.test(pw)) s += 1;
  if (/[^A-Za-z0-9]/.test(pw)) s += 1;
  return s;
};
const STRENGTH_TONE = {
  1: { bg: 'bg-rose-500',    text: 'text-rose-600 dark:text-rose-400',       label: 'Weak' },
  2: { bg: 'bg-amber-500',   text: 'text-amber-600 dark:text-amber-400',     label: 'Fair' },
  3: { bg: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', label: 'Strong' },
  4: { bg: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-300', label: 'Excellent' },
};

// Role tile + chip palette — same hues as the list-pages role chips.
// Participants aren't created from this form — they sign up via a session
// code, so the tile is intentionally absent.
const ROLE_TILE = {
  trainer: {
    Icon: GraduationCap,
    title: 'Trainer',
    desc: 'Runs sessions, manages workbooks.',
    chip: 'bg-cyan-50 text-cyan-600 ring-cyan-100 dark:bg-cyan-500/10 dark:text-cyan-400 dark:ring-cyan-500/20',
    pillCls: 'bg-cyan-50 text-cyan-700 ring-cyan-200 dark:bg-cyan-500/10 dark:text-cyan-300 dark:ring-cyan-500/30',
  },
  client: {
    Icon: Briefcase,
    title: 'Client',
    desc: 'Manages an organization and its trainers.',
    chip: 'bg-violet-50 text-violet-600 ring-violet-100 dark:bg-violet-500/10 dark:text-violet-400 dark:ring-violet-500/20',
    pillCls: 'bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/30',
  },
  'super-admin': {
    Icon: ShieldCheck,
    title: 'Super Admin',
    desc: 'Full platform access. Use sparingly.',
    chip: 'bg-rose-50 text-rose-600 ring-rose-100 dark:bg-rose-500/10 dark:text-rose-400 dark:ring-rose-500/20',
    pillCls: 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/30',
  },
};
const SOLO_PILL = 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30';

const FIELD_LABELS = {
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  phone: 'Phone',
  organizationId: 'Organization',
  password: 'Password',
  confirmPassword: 'Confirm password',
};

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
];

const AddUserPage = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const emailCheck = useEmailCheck(userService.checkEmail);
  const formRef = useRef(null);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setOrgsLoading(true);
      const response = await organizationService.getOrganizations({ limit: 100 });
      setOrganizations(response.data || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setOrgsLoading(false);
    }
  };

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'trainer',
    organizationId: '',
    isIndependentTrainer: false,
    plan: 'free', // solo trainers only: admin-chosen starting plan (free/pro), no payment
    status: 'active',
    department: '',
    jobTitle: '',
    password: '',
    confirmPassword: '',
    bio: '',
    expertise: [],
    expertiseInput: '',
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    if (name === 'email') emailCheck.checkEmail(value);
    if (name === 'password') {
      if (value.length > 0 && value.length < 8) {
        setErrors((prev) => ({ ...prev, password: 'Password must be at least 8 characters' }));
      } else {
        setErrors((prev) => ({ ...prev, password: '' }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
    else if (emailCheck.available === false) newErrors.email = 'This email is already in use';
    if (formData.phone && !/^\+?[\d\s\-()]+$/.test(formData.phone)) newErrors.phone = 'Invalid phone number format';
    if ((formData.role === 'client' || (formData.role === 'trainer' && !formData.isIndependentTrainer)) && !formData.organizationId) {
      newErrors.organizationId = 'Organization is required for this role';
    }
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix the form errors');
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    setIsSubmitting(true);
    try {
      const { confirmPassword, organizationId, expertiseInput, isIndependentTrainer, ...userData } = formData;
      const submitData = {
        ...userData,
        organization: isIndependentTrainer ? null : (organizationId || null),
        isIndependentTrainer: formData.role === 'trainer' ? isIndependentTrainer : undefined,
      };
      if (formData.role !== 'trainer') {
        delete submitData.bio;
        delete submitData.expertise;
      }
      await userService.createUser(submitData);
      toast.success('User created successfully!');
      navigate('/admin/users');
    } catch (error) {
      toast.error(error.message || 'Failed to create user');
      console.error('Error creating user:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => navigate('/admin/users');

  const handleAddExpertise = () => {
    if (formData.expertiseInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        expertise: [...prev.expertise, prev.expertiseInput.trim()],
        expertiseInput: '',
      }));
    }
  };
  const handleRemoveExpertise = (index) => {
    setFormData((prev) => ({
      ...prev,
      expertise: prev.expertise.filter((_, i) => i !== index),
    }));
  };

  // ⌘+Enter / Ctrl+Enter submit shortcut.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !isSubmitting) {
        e.preventDefault();
        formRef.current?.requestSubmit?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isSubmitting]);

  // Progress checklist — derived from the same checks validateForm runs.
  const validEmail = formData.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) && emailCheck.available !== false;
  const roleOrgDone =
    !!formData.role &&
    (formData.isIndependentTrainer || !!formData.organizationId || formData.role === 'super-admin');
  const sectionDone = {
    personal: formData.firstName.trim() !== '' && formData.lastName.trim() !== '' && validEmail,
    role: roleOrgDone,
    password: formData.password.length >= 8 && formData.password === formData.confirmPassword,
  };

  // Live preview values
  const initials = `${(formData.firstName || '').charAt(0)}${(formData.lastName || '').charAt(0)}`.toUpperCase();
  const previewGradient = seededPreviewGradient(initials || 'AA');
  const fullName = `${formData.firstName} ${formData.lastName}`.trim();
  const role = ROLE_TILE[formData.role] || ROLE_TILE.trainer;
  const isSolo = formData.role === 'trainer' && formData.isIndependentTrainer;
  const previewOrgName = isSolo
    ? 'Independent'
    : (organizations.find((o) => o._id === formData.organizationId)?.name || '—');

  const pwStrength = passwordStrength(formData.password);
  const pwTone = STRENGTH_TONE[pwStrength];
  const passwordsMatch = formData.confirmPassword.length > 0 && formData.password === formData.confirmPassword;

  const errorKeys = Object.keys(errors).filter((k) => !!errors[k]);
  const errorLabelList = errorKeys.map((k) => FIELD_LABELS[k] || k).join(', ');

  // Whether the org dropdown should appear (existing logic preserved)
  const showOrgSelect = formData.role === 'client' || (formData.role === 'trainer' && !formData.isIndependentTrainer);

  return (
    <DashboardLayout role="super-admin">
      <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)] gap-6 w-full px-4 lg:px-6 py-6">
        {/* ────── LEFT RAIL ────── */}
        <aside className="lg:sticky lg:top-6 lg:self-start space-y-4">
          {/* A. Page identity */}
          <div className={`${CARD_BASE} p-5`}>
            <span className="inline-flex w-10 h-10 rounded-xl items-center justify-center bg-primary-50 ring-1 ring-inset ring-primary-100 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400 dark:ring-primary-500/20">
              <UserPlus size={18} strokeWidth={1.75} />
            </span>
            <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-50 mt-3">New user</h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Create a new user and assign their role.</p>
            <button
              type="button"
              onClick={() => navigate('/admin/users')}
              className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 inline-flex items-center gap-1 mt-4 transition-colors"
            >
              <ArrowLeft size={14} strokeWidth={1.75} />
              Back to Users
            </button>
          </div>

          {/* B. Live preview */}
          <div className={`${CARD_BASE} overflow-hidden`}>
            <div className="bg-gradient-to-r from-neutral-50 to-white dark:from-neutral-900 dark:to-neutral-900/60 px-4 py-2 text-[10px] uppercase tracking-[0.08em] font-semibold text-neutral-400 border-b border-neutral-100 dark:border-neutral-800/70">
              Preview
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-3">
                {initials ? (
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${previewGradient} flex items-center justify-center text-white font-semibold text-sm shadow-sm`}>
                    {initials}
                  </div>
                ) : (
                  <div className="w-11 h-11 rounded-xl border-2 border-dashed border-neutral-200 dark:border-neutral-700 flex items-center justify-center text-neutral-300 dark:text-neutral-600 text-xs font-semibold">
                    AA
                  </div>
                )}
                <div className="min-w-0">
                  <p className={`text-sm font-semibold truncate ${fullName ? 'text-neutral-900 dark:text-neutral-100' : 'text-neutral-400'}`}>
                    {fullName || 'Full name'}
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{formData.email || '—'}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ring-inset capitalize ${role.pillCls}`}>
                  {role.title}
                </span>
                {isSolo && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ring-inset ${SOLO_PILL}`}>
                    Solo
                  </span>
                )}
              </div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">{previewOrgName}</p>
            </div>
          </div>

          {/* C. Progress */}
          <div className={`${CARD_BASE} p-4`}>
            <p className="text-[11px] uppercase tracking-[0.06em] font-semibold text-neutral-400 mb-2">Progress</p>
            {[
              { key: 'personal', label: 'Personal information', done: sectionDone.personal },
              { key: 'role', label: 'Role & organization', done: sectionDone.role },
              { key: 'password', label: 'Account credentials', done: sectionDone.password },
            ].map(({ key, label, done }) => (
              <div key={key} className="flex items-center gap-2 py-1.5">
                {done
                  ? <CheckCircle2 size={14} className="text-emerald-500" />
                  : <Circle size={14} className="text-neutral-300 dark:text-neutral-600" />}
                <span className={`text-sm ${done ? 'text-neutral-700 dark:text-neutral-200' : 'text-neutral-400'}`}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        </aside>

        {/* ────── MAIN FORM ────── */}
        <main>
          <form ref={formRef} onSubmit={handleSubmit} className={CARD_FORM}>
            {/* Header */}
            <div className="px-6 lg:px-8 pt-6 pb-5 border-b border-neutral-100 dark:border-neutral-800/70">
              <h1 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Create user</h1>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Fill in the details below — required fields are marked with <span className="text-rose-500">*</span>.</p>
            </div>

            <div className="p-6 lg:p-8 space-y-10">
              {/* Error summary */}
              {errorKeys.length > 0 && (
                <div className="rounded-xl bg-rose-50 ring-1 ring-inset ring-rose-200 px-4 py-3 flex items-start gap-3 dark:bg-rose-500/10 dark:ring-rose-500/20">
                  <AlertCircle size={18} strokeWidth={1.75} className="text-rose-600 dark:text-rose-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-rose-900 dark:text-rose-200">Please fix the errors below</p>
                    <p className="text-xs text-rose-700/80 dark:text-rose-300/80 mt-0.5">{errorLabelList}</p>
                  </div>
                </div>
              )}

              {/* ── Personal information ── */}
              <section>
                <SectionHeader step="01" title="Personal information" subtitle="How this person identifies on the platform." />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="Enter first name" required fullWidth error={errors.firstName} />
                  <Input label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Enter last name" required fullWidth error={errors.lastName} />

                  <Input
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="user@example.com"
                    required
                    fullWidth
                    error={errors.email || emailCheck.error}
                    helperText={!errors.email && !emailCheck.error && emailCheck.available === true ? 'Email is available' : undefined}
                    rightIcon={
                      formData.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) ? (
                        emailCheck.checking ? <Loader2 size={18} className="animate-spin text-neutral-400" /> :
                        emailCheck.available === true ? <CheckCircle2 size={18} className="text-emerald-500" /> :
                        emailCheck.available === false ? <XCircle size={18} className="text-rose-500" /> : null
                      ) : null
                    }
                  />

                  {/* Phone — visual "+" prefix */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1.5">
                      Phone Number
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm pointer-events-none">+</span>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="1 (555) 123-4567"
                        className={`w-full pl-7 pr-3 py-2.5 text-sm rounded-xl bg-white dark:bg-neutral-900 ring-1 ring-inset ${errors.phone ? 'ring-rose-300 focus:ring-rose-500/40' : 'ring-neutral-200 dark:ring-neutral-800 focus:ring-primary-500/30'} text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:outline-none focus:ring-2 transition`}
                      />
                    </div>
                    {errors.phone && <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">{errors.phone}</p>}
                  </div>
                </div>
              </section>

              <div className="border-t border-neutral-100 dark:border-neutral-800/60" />

              {/* ── Role & organization ── */}
              <section>
                <SectionHeader step="02" title="Role & organization" subtitle="What they can access and which org they belong to." />

                {/* Role tiles */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                  {Object.entries(ROLE_TILE).map(([value, def]) => {
                    const RoleIcon = def.Icon;
                    const selected = formData.role === value;
                    return (
                      <label
                        key={value}
                        className={`relative block rounded-2xl bg-white dark:bg-neutral-900 p-4 cursor-pointer text-left transition-all ${
                          selected
                            ? 'ring-2 ring-primary-500 shadow-[0_1px_3px_rgba(16,24,40,0.06),0_8px_20px_-12px_rgba(255,138,0,0.25)] bg-primary-50/30 dark:bg-primary-500/5'
                            : 'ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 hover:ring-neutral-300 dark:hover:ring-neutral-700'
                        }`}
                      >
                        <input
                          type="radio"
                          name="role"
                          value={value}
                          checked={selected}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        {selected && (
                          <span className="absolute top-2.5 right-2.5 inline-flex w-5 h-5 rounded-full bg-primary-500 text-white items-center justify-center">
                            <CheckCircle2 size={14} strokeWidth={2.25} />
                          </span>
                        )}
                        <span className={`inline-flex w-9 h-9 rounded-xl items-center justify-center ring-1 ring-inset ${def.chip}`}>
                          <RoleIcon size={18} strokeWidth={1.75} />
                        </span>
                        <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mt-3">{def.title}</p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 leading-relaxed">{def.desc}</p>
                      </label>
                    );
                  })}
                </div>

                {/* Independent trainer toggle */}
                {formData.role === 'trainer' && (
                  <div className="rounded-xl bg-amber-50/60 dark:bg-amber-500/10 ring-1 ring-inset ring-amber-200/60 dark:ring-amber-500/20 p-3 flex items-center gap-3 mb-4">
                    <Star size={18} strokeWidth={1.75} className="text-amber-600 dark:text-amber-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-amber-900 dark:text-amber-200">Independent Trainer</p>
                      <p className="text-xs text-amber-700/80 dark:text-amber-300/80">Solo trainer with their own organization and billing.</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        name="isIndependentTrainer"
                        checked={formData.isIndependentTrainer}
                        onChange={(e) => setFormData((prev) => ({
                          ...prev,
                          isIndependentTrainer: e.target.checked,
                          organizationId: e.target.checked ? '' : prev.organizationId,
                        }))}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-neutral-300 dark:bg-neutral-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-500/40 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-500" />
                    </label>
                  </div>
                )}

                {/* Solo trainer plan selection — admin can grant Pro for free */}
                {isSolo && (
                  <div className="rounded-xl bg-neutral-50 dark:bg-neutral-800/50 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-700/60 p-3 mb-4">
                    <p className="text-sm font-medium text-neutral-800 dark:text-neutral-200 mb-1">Starting plan</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2.5">Choose the trainer's plan. Pro is granted complimentary — no payment required.</p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { value: 'free', label: 'Free', desc: '30 participants' },
                        { value: 'pro', label: 'Pro (complimentary)', desc: '30 participants · full reports' },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, plan: opt.value }))}
                          className={`text-left rounded-lg border-2 px-3 py-2 transition-all ${
                            formData.plan === opt.value
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-500/10'
                              : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300'
                          }`}
                        >
                          <span className="block text-sm font-semibold text-neutral-900 dark:text-neutral-100">{opt.label}</span>
                          <span className="block text-[11px] text-neutral-500 dark:text-neutral-400">{opt.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  {/* Organization select gets its own row when present */}
                  {showOrgSelect && (
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1.5">
                        Organization <span className="text-rose-500">*</span>
                      </label>
                      {orgsLoading ? (
                        <div className="animate-pulse h-10 rounded-xl bg-neutral-100 dark:bg-neutral-800" />
                      ) : (
                        <div className="relative">
                          <select
                            name="organizationId"
                            value={formData.organizationId}
                            onChange={handleChange}
                            required
                            className={`appearance-none w-full pl-3 pr-9 py-2.5 text-sm rounded-xl bg-white dark:bg-neutral-900 ring-1 ring-inset ${errors.organizationId ? 'ring-rose-300 focus:ring-rose-500/40' : 'ring-neutral-200 dark:ring-neutral-800 focus:ring-primary-500/30'} text-neutral-700 dark:text-neutral-200 focus:outline-none focus:ring-2 transition cursor-pointer`}
                          >
                            <option value="">Select an organization</option>
                            {organizations.map((org) => (
                              <option key={org._id} value={org._id}>{org.name}</option>
                            ))}
                          </select>
                          <ChevronDown size={14} strokeWidth={1.75} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-neutral-400" />
                        </div>
                      )}
                      {errors.organizationId && <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">{errors.organizationId}</p>}
                    </div>
                  )}

                  {/* Job title / department for non-trainer */}
                  {formData.role !== 'trainer' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input label="Job Title" name="jobTitle" value={formData.jobTitle} onChange={handleChange} placeholder="Enter job title" fullWidth />
                      <Input label="Department" name="department" value={formData.department} onChange={handleChange} placeholder="Enter department" fullWidth />
                    </div>
                  )}

                  {/* Status as a proper segmented control on its own row */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1.5">
                      Status <span className="text-rose-500">*</span>
                    </label>
                    <div
                      className="inline-flex items-center p-1 rounded-xl bg-neutral-100 dark:bg-neutral-800 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800"
                      role="group"
                      aria-label="Status"
                    >
                      {STATUS_OPTIONS.map((opt) => {
                        const active = formData.status === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, status: opt.value }))}
                            aria-pressed={active}
                            className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${
                              active
                                ? 'bg-white dark:bg-neutral-900 text-primary-700 dark:text-primary-300 shadow-sm ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-700'
                                : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200'
                            }`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </section>

              <div className="border-t border-neutral-100 dark:border-neutral-800/60" />

              {/* ── Trainer profile ── */}
              {formData.role === 'trainer' && (
                <section>
                  <SectionHeader step="03" title="Trainer profile" subtitle="Bio and expertise areas." />
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1.5">Bio</label>
                      <textarea
                        name="bio"
                        value={formData.bio}
                        onChange={handleChange}
                        placeholder="Brief description about the trainer…"
                        rows={3}
                        className="w-full px-3 py-2.5 text-sm rounded-xl bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 transition resize-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1.5">Areas of Expertise</label>
                      <div className="flex gap-2">
                        <Input
                          name="expertiseInput"
                          value={formData.expertiseInput}
                          onChange={handleChange}
                          placeholder="e.g., Leadership, Sales"
                          fullWidth
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddExpertise();
                            }
                          }}
                        />
                        <Button type="button" variant="secondary" onClick={handleAddExpertise}>Add</Button>
                      </div>
                      {formData.expertise.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {formData.expertise.map((exp, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center gap-1 ring-1 ring-inset ring-neutral-200 bg-white px-2 py-0.5 rounded-full text-xs text-neutral-700 dark:bg-neutral-900 dark:ring-neutral-800 dark:text-neutral-200"
                            >
                              {exp}
                              <button
                                type="button"
                                onClick={() => handleRemoveExpertise(index)}
                                aria-label={`Remove ${exp}`}
                                className="text-neutral-400 hover:text-rose-600 ml-1 transition-colors"
                              >
                                <X size={12} strokeWidth={2} />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              )}

              {/* Divider before credentials block */}
              <div className="border-t border-neutral-100 dark:border-neutral-800/60" />

              {/* ── Account credentials ── */}
              <section>
                <SectionHeader step={formData.role === 'trainer' ? '04' : '03'} title="Account credentials" subtitle="Set the initial password — they can change it later." />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Input
                      label="Password"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter password"
                      required
                      fullWidth
                      error={errors.password}
                    />
                    {formData.password.length > 0 && pwStrength > 0 && (
                      <>
                        <div className="mt-2 flex gap-1">
                          {[0, 1, 2, 3].map((i) => (
                            <span
                              key={i}
                              className={`h-1 flex-1 rounded-full ${i < pwStrength ? pwTone.bg : 'bg-neutral-200 dark:bg-neutral-800'}`}
                            />
                          ))}
                        </div>
                        <p className={`text-[11px] mt-1 ${pwTone.text}`}>{pwTone.label}</p>
                      </>
                    )}
                    {!errors.password && formData.password.length === 0 && (
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">Must be at least 8 characters</p>
                    )}
                  </div>
                  <div>
                    <Input
                      label="Confirm Password"
                      name="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm password"
                      required
                      fullWidth
                      error={errors.confirmPassword}
                    />
                    {formData.password.length > 0 && formData.confirmPassword.length > 0 && (
                      <p className={`flex items-center gap-1 text-[11px] mt-1 ${passwordsMatch ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                        {passwordsMatch ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                        {passwordsMatch ? 'Passwords match' : "Passwords don't match"}
                      </p>
                    )}
                  </div>
                </div>
              </section>
            </div>

            {/* Sticky footer */}
            <div className="sticky bottom-0 z-10 bg-white/90 dark:bg-neutral-900/90 backdrop-blur-md border-t border-neutral-100 dark:border-neutral-800/70 px-6 lg:px-8 py-4 flex items-center justify-between gap-3">
              <p className="text-xs text-neutral-500 dark:text-neutral-400 hidden sm:flex items-center gap-1.5">
                <kbd className="inline-flex items-center px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-700 text-[10px] font-mono text-neutral-600 dark:text-neutral-300">⌘ Enter</kbd>
                to submit
              </p>
              <div className="flex items-center gap-2 ml-auto">
                <Button variant="ghost" type="button" onClick={handleCancel} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <><Loader2 size={14} className="animate-spin mr-1.5" /> Creating…</>
                  ) : (
                    <><Plus size={14} className="mr-1.5" /> Create User</>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </main>
      </div>
    </DashboardLayout>
  );
};

// Numbered step badge gives the form an implicit progress signal without
// adding another column. Falls back to an Icon if step isn't supplied.
const SectionHeader = ({ step, Icon, title, subtitle }) => (
  <div className="flex items-center gap-3 mb-5">
    <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-primary-50 dark:bg-primary-500/10 ring-1 ring-inset ring-primary-100 dark:ring-primary-500/20 text-primary-700 dark:text-primary-300 text-xs font-semibold tracking-wider">
      {step ? step : Icon ? <Icon size={15} strokeWidth={1.75} /> : null}
    </span>
    <div>
      <h2 className="text-[15px] font-semibold text-neutral-900 dark:text-neutral-100 leading-tight">{title}</h2>
      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{subtitle}</p>
    </div>
  </div>
);

export default AddUserPage;
